import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';

import { ChatGroupApi, ChatGroupListResponse } from '../api/chatGroupApi';
import { ChatGroupRoomApi, ChatGroupRoomListResponse, MAX_SEARCH_API_LIMIT } from '../api/chatGroupRoomApi';
import { ChatGroup, GroupType } from '../model/chatGroup';
import { ChatGroupRoom } from '../model/chatGroupRoom';
import { Pagination } from '../model/pagination';
import { RootState } from '../rootReducer';
import { getAllResources, getAllResourcesWithParams } from '../utils/pagination';
import { withTopError } from './errorSlice';
import { ThunkConfig } from './thunkConfig';

const GROUP_PER_PAGE = 10;
const ROOM_PER_PAGE = 10;

export const getChatGroups = createAsyncThunk<ChatGroupListResponse[], void, ThunkConfig>(
    `group/getChatGroups`,
    withTopError(async (_, thunkAPI) => {
        const offset = thunkAPI.getState().chatGroup.groupPagination.offset;
        return await ChatGroupApi.recent(offset, GROUP_PER_PAGE + 1, [GroupType.GROUP]);
    }, i18next.t('error.internalServerError'))
);

export const getFriendGroups = createAsyncThunk<
    { group: ChatGroupListResponse; rooms: ChatGroupRoomListResponse[] }[],
    void,
    ThunkConfig
>(
    `group/getFriendGroups`,
    withTopError(async (_, thunkAPI) => {
        const offset = thunkAPI.getState().chatGroup.friendGroupPagination.offset;
        const chatGroups = await ChatGroupApi.recent(offset, GROUP_PER_PAGE + 1, [GroupType.INDIVIDUAL]);
        const promises = chatGroups.map((group) =>
            ChatGroupRoomApi(group.id)
                .search(0, ROOM_PER_PAGE)
                .then((chat_group_rooms) => ({
                    rooms: chat_group_rooms,
                    group: group,
                }))
        );
        return await Promise.all(promises);
    }, i18next.t('error.internalServerError'))
);

export const getRooms = createAsyncThunk<{ rooms: ChatGroupRoomListResponse[]; groupId: number }, number, ThunkConfig>(
    `group/getRooms`,
    withTopError(async (groupId, thunkAPI) => {
        const group = thunkAPI.getState().chatGroup.groups.find((g) => g.id === groupId);
        if (group) {
            const rooms = await ChatGroupRoomApi(groupId).search(group.offset, ROOM_PER_PAGE + 1);
            return {
                rooms: rooms,
                groupId: groupId,
            };
        }
        return {
            rooms: [],
            groupId: groupId,
        };
    }, i18next.t('error.internalServerError'))
);

export const updateRooms = createAsyncThunk<
    ChatGroupRoomListResponse[],
    { groupId: number; roomIds: number[] },
    ThunkConfig
>(
    `group/updateRooms`,
    withTopError(async (params, ThunkAPI) => {
        const state = ThunkAPI.getState().chatGroup;
        const storedRoomIds = [...state.groups, ...state.friendGroups].flatMap((g) => g.chatRooms).map((r) => r.id);
        // Even If you execute API, if the room isn't in store, the room is not updated. So in advance, filter such roomIds
        const filteredRoomIds = params.roomIds.filter((rId) => storedRoomIds.includes(rId));
        if (filteredRoomIds.length < 1) {
            return [];
        }

        const roomsPromises = getAllResourcesWithParams(filteredRoomIds, ROOM_PER_PAGE, async (limitedParams) => {
            return ChatGroupRoomApi(params.groupId).batchGet(limitedParams);
        });
        return await Promise.all(roomsPromises).then((res) => res.flat());
    }, i18next.t('error.internalServerError'))
);

// 現在のoffsetで表示されている全てのroomを再取得する
// もし、apiのresponseに含まれているroomで、storeに存在しないroomだった場合、そのroomはstoreから追加される
// もし、apiのresponseに含まれていないroomで、storeに存在するroomだった場合、そのroomはstoreに削除される
export const refetchAllRooms = createAsyncThunk<(ChatGroupRoomListResponse & { groupId: number })[], void, ThunkConfig>(
    'group/refetchAllRooms',
    withTopError(async (_, thunkAPI) => {
        const state = thunkAPI.getState().chatGroup;
        const groups: { id: number; offset?: number }[] = [...state.groups, ...state.friendGroups];

        const promises = groups.map((group) => {
            // When group.offset isn't undefined, the group is in state.groups. So the maximum number of rooms is group.offset + ROOM_PER_PAGE
            // When group.offset is undefined, the group is in state.friendGroups. So the maximum number of rooms is 2 (less than ROOM_PER_PAGE)
            const n = group.offset ? group.offset + ROOM_PER_PAGE : ROOM_PER_PAGE;
            const roomsPromises = getAllResources(n, MAX_SEARCH_API_LIMIT, 0, async (limit, offset) =>
                ChatGroupRoomApi(group.id).search(offset, limit)
            );
            return Promise.all(roomsPromises).then((res) =>
                res.flat().map((r) => ({
                    ...r,
                    groupId: group.id, // add groupId for distinguishing what group the room belongs to
                }))
            );
        });
        return await Promise.all(promises).then((res) => res.flat());
    }, i18next.t('error.internalServerError'))
);

export type ChatGroupState = {
    // each group has pagination for rooms
    groups: (ChatGroup & Pagination)[];
    groupPagination: Pagination;
    friendGroups: ChatGroup[];
    friendGroupPagination: Pagination;
    selectedChatGroup: ChatGroup | undefined;
    selectedChatRoom: ChatGroupRoom | undefined;
};

const initialState: ChatGroupState = {
    groups: [],
    groupPagination: {
        offset: 0,
        hasMore: false,
    },
    friendGroups: [],
    friendGroupPagination: {
        offset: 0,
        hasMore: false,
    },
    selectedChatGroup: undefined,
    selectedChatRoom: undefined,
};

export const chatGroupSlice = createSlice({
    name: 'chatGroups',
    initialState,
    reducers: {
        selectGroup: (state, { payload }: PayloadAction<number>) => {
            const groups = [...state.groups, ...state.friendGroups];
            state.selectedChatGroup = groups.find((group) => group.id === payload);
        },
        selectRoom: (state, { payload }: PayloadAction<{ group: ChatGroup; room: ChatGroupRoom }>) => {
            state.selectedChatGroup = payload.group;
            state.selectedChatRoom = payload.room;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getChatGroups.fulfilled, (state, action) => {
            // shallow copy
            const groupsJson = [...action.payload];
            if (groupsJson.length > GROUP_PER_PAGE) {
                groupsJson.pop(); // groupsJson has GROUP_PER_PAGE + 1 elements.
            }

            const groups = state.groups;
            const groupIds = groups.map((g) => g.id);
            const newGroups = groupsJson
                .map((group) => {
                    const newGroup = makeGroupFromJson(group, []);
                    return {
                        ...newGroup,
                        offset: 0,
                        hasMore: false,
                    };
                })
                .filter((g) => !groupIds.includes(g.id));

            state.groups = [...groups, ...newGroups];
            state.groupPagination.offset += GROUP_PER_PAGE;
            state.groupPagination.hasMore = action.payload.length > GROUP_PER_PAGE;
        });
        builder.addCase(getFriendGroups.fulfilled, (state, action) => {
            // shallow copy
            const groupsJson = [...action.payload];
            if (groupsJson.length > GROUP_PER_PAGE) {
                groupsJson.pop(); // groupsJson has GROUP_PER_PAGE + 1 elements.
            }

            const groups = state.friendGroups;
            const groupIds = groups.map((g) => g.id);
            const newGroups = groupsJson
                .map((groupWithRooms) => makeGroupFromJson(groupWithRooms.group, groupWithRooms.rooms))
                .filter((g) => !groupIds.includes(g.id));

            state.friendGroups = [...groups, ...newGroups];
            state.friendGroupPagination.offset += GROUP_PER_PAGE;
            state.friendGroupPagination.hasMore = action.payload.length > GROUP_PER_PAGE;
        });
        builder.addCase(getRooms.fulfilled, (state, action) => {
            const groupId = action.payload.groupId;
            const roomsJson = [...action.payload.rooms];
            if (roomsJson.length > ROOM_PER_PAGE) {
                roomsJson.pop(); // roomsJson has ROOM_PER_PAGE + 1 elements
            }

            state.groups = state.groups.map((group) => {
                if (group.id !== groupId) {
                    return group;
                }

                const oldRooms = group.chatRooms;
                const newRooms = roomsJson.map((json) => makeRoomFromJson(json));
                return {
                    ...group,
                    chatRooms: [...oldRooms, ...newRooms],
                    offset: group.offset + ROOM_PER_PAGE,
                    hasMore: action.payload.rooms.length > ROOM_PER_PAGE,
                };
            });
        });
        builder.addCase(updateRooms.fulfilled, (state, action) => {
            const newRooms = action.payload.map((r) => makeRoomFromJson(r));
            state.groups = updateGroupsByRooms(state.groups, newRooms);
            state.friendGroups = updateGroupsByRooms(state.friendGroups, newRooms);
        });
        builder.addCase(refetchAllRooms.fulfilled, (state, action) => {
            state.groups = state.groups.map((group) => {
                const rooms = action.payload.filter((r) => r.groupId === group.id);
                return { ...group, chatRooms: rooms.map((r) => makeRoomFromJson(r)) };
            });
            state.friendGroups = state.friendGroups.map((group) => {
                const rooms = action.payload.filter((r) => r.groupId === group.id);
                return { ...group, chatRooms: rooms.map((r) => makeRoomFromJson(r)) };
            });
        });
    },
});

const makeGroupFromJson = (group: ChatGroupListResponse, rooms: ChatGroupRoomListResponse[]) => {
    const members = group.users.map((user) => ({
        id: user.id,
        name: user.name,
        icon: user.user_image,
    }));

    const chatGroupRooms: ChatGroupRoom[] = rooms.map((room) => makeRoomFromJson(room));
    return {
        id: group.id,
        type: group.type,
        name: group.name,
        icon: group.image,
        members: members,
        chatRooms: chatGroupRooms,
    };
};

const makeRoomFromJson = (room: ChatGroupRoomListResponse) => {
    const users = room.users.map((user) => ({
        id: user.id,
        icon: user.user_image,
        name: user.name,
    }));
    return {
        id: room.id,
        name: room.name,
        isLock: room.is_lock,
        members: users,
    };
};

// type params T is ChatGroup | Pagination (state.groups) or ChatGroup (state.friendGroups)
// userがアクセス中にroomが増減した場合は考慮しない
const updateGroupsByRooms = <T extends ChatGroup>(groups: T[], rooms: ChatGroupRoom[]) =>
    groups.map((group) => {
        const updatedRooms = group.chatRooms.map((oldRoom) => {
            const newRoom = rooms.find((r) => r.id === oldRoom.id);
            if (newRoom !== undefined) {
                return newRoom;
            }
            return oldRoom;
        });
        return {
            ...group,
            chatRooms: updatedRooms,
        };
    });

export const { selectGroup, selectRoom } = chatGroupSlice.actions;

export const selectChatGroup = (state: RootState): ChatGroupState => state.chatGroup;

export const chatGroupReducer = chatGroupSlice.reducer;
